import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Phone } from "lucide-react";
import { Button } from "@/components/ui/button";

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

const links = [
  { label: "How it works", id: "manifesto" },
  { label: "Services", id: "services" },
  { label: "Why us", id: "why" },
  { label: "Book a meeting", id: "book" },
  { label: "FAQ", id: "faq" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,box-shadow,backdrop-filter] duration-500 ${
        scrolled ? "bg-cream/85 backdrop-blur-md shadow-[0_1px_0_rgba(0,0,0,0.06)]" : "bg-transparent"
      }`}
      data-testid="navbar"
    >
      <nav className="mx-auto max-w-7xl px-6 lg:px-10 h-20 flex items-center justify-between">
        <button
          onClick={() => scrollTo("hero")}
          className="flex items-center gap-2.5 group"
          data-testid="nav-logo"
        >
          <span className={`block w-10 h-10 rounded-full grid place-items-center ring-1 transition-all duration-300 group-hover:-rotate-6 ${
            scrolled ? "bg-forest text-cream ring-forest/15" : "bg-cream text-forest ring-cream/30"
          }`}>
            <span className="font-serif italic text-lg leading-none">A</span>
          </span>
          <span className={`font-serif text-2xl tracking-tight transition-colors ${scrolled ? "text-forest" : "text-cream"}`}>Advanced Life Policies</span>
        </button>

        <div className="hidden md:flex items-center gap-9">
          {links.map((l) => (
            <button
              key={l.id}
              onClick={() => scrollTo(l.id)}
              data-testid={`nav-link-${l.id}`}
              className={`relative text-sm font-medium transition-colors after:absolute after:-bottom-1.5 after:left-0 after:h-px after:w-0 after:bg-terracotta after:transition-all after:duration-300 hover:after:w-full ${
                scrolled ? "text-forest/80 hover:text-forest" : "text-cream/80 hover:text-cream"
              }`}
            >
              {l.label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <a
            href="tel:+19179053568"
            data-testid="nav-phone"
            className={`hidden lg:inline-flex items-center gap-1.5 text-sm font-semibold transition-colors ${scrolled ? "text-forest/80 hover:text-forest" : "text-cream/80 hover:text-cream"}`}
          >
            <Phone className="h-4 w-4 text-terracotta" /> (917) 905-3568
          </a>
          <button
            onClick={() => scrollTo("hero")}
            data-testid="nav-quote"
            className={`hidden sm:inline text-sm font-semibold transition-colors ${scrolled ? "text-forest/80 hover:text-forest" : "text-cream/80 hover:text-cream"}`}
          >
            Get a quote
          </button>
          <Button
            onClick={() => navigate("/apply")}
            data-testid="nav-cta"
            className="rounded-full bg-terracotta hover:bg-terracotta-dark text-cream px-6 h-11 font-semibold shadow-sm transition-all duration-300 hover:-translate-y-0.5 active:scale-95"
          >
            Apply now
          </Button>
        </div>
      </nav>
    </motion.header>
  );
}
